/**
 * Linkerpaneel: de structuur van de website.
 *
 * De homepage staat altijd bovenaan en is als zodanig herkenbaar, want dat
 * is het enige structurele begrip dat het contentmodel kent. Verwijderen van
 * de homepage wordt vóóraf geblokkeerd met uitleg, niet achteraf met een
 * foutmelding.
 */
import React from 'react';
import { FileText, Home, Copy, MoreHorizontal, Plus, Settings2, Star, Trash2 } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { displayPath } from '../slug';
import type { SitePage } from '../types';

interface PagesPanelProps {
  pages: SitePage[];
  selectedPageId: string | null;
  onSelect: (pageId: string) => void;
  onAdd: () => void;
  onEdit: (pageId: string) => void;
  onMakeHomepage: (pageId: string) => void;
  onDelete: (pageId: string) => void;
  onDuplicate: (pageId: string) => void;
  deletionBlockedReason: (pageId: string) => string | null;
}

export function PagesPanel({
  pages,
  selectedPageId,
  onSelect,
  onAdd,
  onEdit,
  onMakeHomepage,
  onDelete,
  onDuplicate,
  deletionBlockedReason,
}: PagesPanelProps) {
  const ordered = [...pages].sort((a, b) => {
    if (a.slug === b.slug) return 0;
    if (a.slug === '') return -1;
    if (b.slug === '') return 1;
    return 0;
  });

  return (
    <aside className="flex h-full w-full flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
          Pagina&apos;s
        </h2>
        <button
          type="button"
          onClick={onAdd}
          data-testid="sites-add-page"
          aria-label="Pagina toevoegen"
          title="Pagina toevoegen"
          className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-teal-700"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {ordered.length === 0 ? (
          <div className="mx-2 mt-2 rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center">
            <p className="text-sm font-semibold text-slate-700">Nog geen pagina&apos;s</p>
            <p className="mt-1 text-xs text-slate-500">
              Je eerste pagina wordt automatisch de homepage.
            </p>
            <button
              type="button"
              onClick={onAdd}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700"
            >
              <Plus className="h-3.5 w-3.5" />
              Pagina maken
            </button>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {ordered.map((page) => {
              const isHomepage = page.slug === '';
              const active = page.pageId === selectedPageId;
              const blockedReason = deletionBlockedReason(page.pageId);
              const Icon = isHomepage ? Home : FileText;
              return (
                <li key={page.pageId}>
                  <div
                    className={cn(
                      'group flex items-center gap-2 rounded-xl px-2.5 py-2 transition-colors',
                      active ? 'bg-teal-50' : 'hover:bg-slate-50',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(page.pageId)}
                      data-testid={`sites-page-${page.pageId}`}
                      className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          active ? 'text-teal-700' : 'text-slate-400',
                        )}
                      />
                      <span className="min-w-0">
                        <span
                          className={cn(
                            'block truncate text-sm font-semibold',
                            active ? 'text-teal-900' : 'text-slate-800',
                          )}
                        >
                          {page.title.trim() || 'Naamloze pagina'}
                        </span>
                        <span
                          className={cn(
                            'block truncate text-xs',
                            active ? 'text-teal-700/70' : 'text-slate-400',
                          )}
                        >
                          {displayPath(page.slug)}
                        </span>
                      </span>
                    </button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={`Opties voor ${page.title.trim() || 'naamloze pagina'}`}
                          className={cn(
                            'rounded-lg p-1.5 text-slate-400 transition hover:bg-white hover:text-slate-700',
                            'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
                            active && 'opacity-100',
                          )}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuItem onSelect={() => onEdit(page.pageId)}>
                          <Settings2 className="mr-2 h-4 w-4" />
                          Pagina-instellingen
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => onDuplicate(page.pageId)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Pagina dupliceren
                        </DropdownMenuItem>
                        {!isHomepage && (
                          <DropdownMenuItem onSelect={() => onMakeHomepage(page.pageId)}>
                            <Star className="mr-2 h-4 w-4" />
                            Instellen als homepage
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {blockedReason ? (
                          <div className="px-2 py-2 text-xs leading-snug text-slate-500">
                            {blockedReason}
                          </div>
                        ) : (
                          <DropdownMenuItem
                            onSelect={() => onDelete(page.pageId)}
                            className="text-red-600 focus:bg-red-50 focus:text-red-700"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Pagina verwijderen
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
